import { useQuery } from "@apollo/client/react";
import type { PlainDate } from "@sa-nu/shared-utils/date";
import { toPlainDateString } from "@sa-nu/shared-utils/date";
import { useMemo } from "react";

import type { DocumentType } from "../../../../../__generated__/gql";
import { graphql } from "../../../../../__generated__/gql";
import type { OccupancyLimitTargetType } from "../../../../../__generated__/gql/graphql";
import type { buildAccommodationReservationProviderInput } from "../../../../../utils";
import {
  filterAreasWithAvailability,
  filterAreasByCICO,
} from "../areas/_utils/area-filtering";
import type { CICOSelection } from "../reservation-calendar";

const AreasOnReservationFormQuery = graphql(`
  query AreasOnReservationForm(
    $firstNight: PlainDate
    $lastNight: PlainDate
    $minNight: PlainDate!
    $maxNight: PlainDate!
    $provider: AccommodationReservationProviderInput
    $occupancyLimitTarget: OccupancyLimitTargetType
    $areaId: ID
    $siteId: ID
    $accommodationId: ID
    $withDeferred: Boolean!
    $internal: Boolean!
  ) {
    areasForReservation(provider: $provider, id: $areaId) {
      id
      slug
      name
      ...AreaHeaderFragment
      sitesForReservation(provider: $provider, id: $siteId) {
        id
        accommodationsForReservation(
          provider: $provider
          id: $accommodationId
        ) {
          id
          architectureType {
            id
            name
            series
            seriesShortName
            size
          }
          facilityTypes
          availabilities(
            firstNight: $firstNight
            lastNight: $lastNight
            provider: $provider
            occupancyLimitTarget: $occupancyLimitTarget
            internal: $internal
          ) @include(if: $withDeferred) {
            date
            isAvailable
          }
          isOwned(provider: $provider)
          ...AccommodationsGroupOnCalendar
          ...AccommodationForSiteHeader
        }
        facilities(provider: $provider) {
          ...FacilityForSiteHeader
        }
        ...SiteForHeader
      }
    }
    maybeViewer {
      name
      subscription {
        reservationCyclePeriods(limit: 5) {
          start
          end
          stayedAndReservedNights
          remainingBaseNights
          hasActiveReservation
        }
        next7DaysReservationPeriod {
          start
          end
        }
      }
      organizationMembership {
        role
      }
    }
    peakDays(firstNight: $minNight, lastNight: $maxNight, provider: $provider)
  }
`);

export type AreasOnReservationFormQueryData = DocumentType<
  typeof AreasOnReservationFormQuery
>;

type UseCalendarDataParams = {
  provider: ReturnType<
    typeof buildAccommodationReservationProviderInput
  > | null;
  occupancyLimitTarget: OccupancyLimitTargetType | null;
  focusAreaId: string | null;
  focusSiteId: string | null;
  focusAccommodationId: string | null;
  firstNight: PlainDate;
  lastNight: PlainDate;
  minNight: PlainDate;
  maxNight: PlainDate;
  isInternal: boolean;

  // チェックイン・チェックアウト日付フィルター
  cico: CICOSelection | null;
  alwaysVisibleAccommodationIds: string[];
};

/**
 * カレンダーデータの取得を管理するカスタムフック
 * CICOフィルターによるフィルタリングも行う。
 * CICOフィルターはavailabilityデータのフェッチと同じライフサイクルで動かしたいため、
 * このhookの中で行なってしまっている。
 *
 * フェッチは2段階に分けて行い、フリッカーを防止する:
 * 1. 基本データ (withDeferred: false) - エリア/サイト/宿泊施設の構造
 * 2. 空き状況データ (withDeferred: true) - availabilities を含む
 */
export function useCalendarData({
  provider,
  occupancyLimitTarget,
  focusAreaId,
  focusSiteId,
  focusAccommodationId,
  firstNight,
  lastNight,
  minNight,
  maxNight,
  isInternal,
  cico,
  alwaysVisibleAccommodationIds,
}: UseCalendarDataParams) {
  const variables = {
    provider,
    occupancyLimitTarget,
    areaId: focusAreaId,
    siteId: focusSiteId,
    accommodationId: focusAccommodationId ?? null,
    minNight: toPlainDateString(minNight),
    maxNight: toPlainDateString(maxNight),
  };

  // 1. 基本データの取得（空き状況なし）
  const { data: dataAreas } = useQuery(AreasOnReservationFormQuery, {
    variables: {
      ...variables,
      withDeferred: false,
      internal: isInternal,
    },
    fetchPolicy: "cache-first",
  });

  // 2. 空き状況データの取得
  const { data: dataAreasWithAvailability, loading: loadingAvailability } =
    useQuery(AreasOnReservationFormQuery, {
      variables: {
        ...variables,
        withDeferred: true,
        firstNight: toPlainDateString(firstNight),
        lastNight: toPlainDateString(lastNight),
        internal: isInternal,
      },
      skip: !dataAreas,
      fetchPolicy: "cache-first",
    });

  // Apollo Client の cache-first により、キャッシュがある場合はフリッカーしない
  const data = dataAreasWithAvailability || dataAreas;

  // CICOフィルターは availability データが存在する場合のみ適用。
  // loadingAvailability ではなく dataAreasWithAvailability の有無で判定することで、
  // 日付範囲が変わらない再レンダリング時にフィルターが一瞬解除されるのを防ぐ。
  const hasAvailability = dataAreasWithAvailability != null;

  const allAreas = useMemo(() => {
    if (!data) return [];
    return filterAreasWithAvailability(data.areasForReservation, lastNight);
  }, [data, lastNight]);

  const { displayAreas, displayAreaMap } = useMemo(() => {
    const areas =
      cico && hasAvailability
        ? filterAreasByCICO(allAreas, cico, alwaysVisibleAccommodationIds)
        : allAreas;

    return {
      displayAreas: areas,
      displayAreaMap: new Map(areas.map((area) => [area.id, area])),
    };
  }, [allAreas, cico, hasAvailability, alwaysVisibleAccommodationIds]);

  return {
    /** カレンダー表示用データ */
    data,
    /** 空き状況データのみ（サイクル情報取得用） */
    dataAreasWithAvailability,
    /** 空き状況ローディング中フラグ */
    loadingAvailability,
    /** lastNight トリム後の全エリア（CICO絞り込み前） */
    allAreas,
    /** CICO フィルタ適用後のエリア配列 */
    displayAreas,
    /** CICO フィルタ適用後のエリアMap（O(1) ルックアップ用） */
    displayAreaMap,
  };
}
